/** The letterhead settings' reads and writes (SA-1040 §4.5, D12). The logo rides the existing
 *  media upload; the preview and the logo come back as blobs through the api client. */
import { ApiError, apiClient } from '@/services/apiClient'

const BASE = '/api/admin/quote-document'

export interface QuoteDocumentWrite {
  company_name: string
  address_lines: string[]
  contact_lines: string[]
  legal_line: string
  logo_media_ref: string | null
  logo_mime_type: string | null
  page_size: 'A4' | 'Letter'
  terms: string
  email_subject: string
  email_body: string
}

export interface QuoteDocumentSettings {
  companyName: string
  addressLines: string[]
  contactLines: string[]
  legalLine: string
  logoMediaRef: string | null
  logoMimeType: string | null
  pageSize: 'A4' | 'Letter'
  terms: string
  emailSubject: string
  emailBody: string
  updatedByName: string | null
  updatedAt: string | null
  companyNameEffective: string
}

type Wire = Record<string, unknown>

function fromWire(w: Wire): QuoteDocumentSettings {
  return {
    companyName: (w.company_name as string) ?? '', addressLines: (w.address_lines as string[]) ?? [],
    contactLines: (w.contact_lines as string[]) ?? [], legalLine: (w.legal_line as string) ?? '',
    logoMediaRef: (w.logo_media_ref as string) ?? null, logoMimeType: (w.logo_mime_type as string) ?? null,
    pageSize: ((w.page_size as 'A4' | 'Letter') ?? 'A4'), terms: (w.terms as string) ?? '',
    emailSubject: (w.email_subject as string) ?? '', emailBody: (w.email_body as string) ?? '',
    updatedByName: (w.updated_by_name as string) ?? null, updatedAt: (w.updated_at as string) ?? null,
    companyNameEffective: (w.company_name_effective as string) ?? '',
  }
}

export async function getQuoteDocumentSettings(): Promise<QuoteDocumentSettings> {
  return fromWire(await apiClient.get<Wire>(BASE))
}

export async function saveQuoteDocumentSettings(write: QuoteDocumentWrite): Promise<QuoteDocumentSettings> {
  return fromWire(await apiClient.put<Wire>(BASE, write))
}

export async function uploadLogo(file: File): Promise<string> {
  const body = new FormData()
  body.append('file', file)
  const res = await apiClient.fetch('/api/media/upload', { method: 'POST', body })
  if (!res.ok) throw new ApiError('upload failed', res.status, await res.json().then((b) => b?.detail).catch(() => null))
  return ((await res.json()) as { media_ref: string }).media_ref
}

/** An object URL for the stored logo, or null when there is none; the caller revokes it. */
export async function fetchLogoUrl(): Promise<string | null> {
  const res = await apiClient.fetch(`${BASE}/logo`)
  if (res.status === 404) return null
  if (!res.ok) throw new ApiError('logo failed', res.status, null)
  return URL.createObjectURL(await res.blob())
}

export async function previewQuoteDocument(): Promise<Blob> {
  const res = await apiClient.fetch(`${BASE}/preview`, { method: 'POST' })
  if (!res.ok) throw new ApiError('preview failed', res.status, await res.json().then((b) => b?.detail).catch(() => null))
  return res.blob()
}
